'use strict';

import PluginManager from './plugin-manager';

import Sizzle from 'sizzle';

import device from '../util/device';
import * as domUtils from '../util/dom';
import debugUtil from '../util/debug';

import extend from '../util/extend';
import breakpoints from '../util/breakpoints';
import Config from '../config';
import VideoWrapperApi from './wrapperApi';
import { createEventWrapper } from '../util/event-wrapper-api';

const Debug = debugUtil('video');

const videoElementConfigAttributes = [
    'autoplay',
    'data-video-controls',
    'preload'
];

const defaultVideoConfig = {
    autoplay: false,
    limitBitrateByPortal: true,
    preload: 'auto'
};

const instances = [];
let _configured = false;

const pluginManager = new PluginManager();

// Array to hold plugins that should be added when the video module is
// configured. It is used to allow addPlugin to be called before initVideo.
const pluginsToAdd = [];

const asArray = (obj) => {
	if (Array.isArray(obj)) {
		return obj;
	}

	if (obj) {
		return [obj];
	}

	return [];
};

function initConfig() {
	// Add plugins listed in the config.
	asArray(Config.plugins)
		.filter((plugin) => plugin)
		.forEach((plugin) => addPlugin(plugin));

	_configured = true;
}

function _createApiProxy(api, element) {
	for (let name of Object.getOwnPropertyNames(Object.getPrototypeOf(api))) {
		if (typeof api[name] === 'function' && name !== 'constructor') {
			if (name === 'destroy') {
				const oldDestroy = element.destroy;
				element.destroy = () => {
					oldDestroy.call(element);
					api.destroy();
				};
			} else {
				element[name] = api[name].bind(api);
			}
		}
	}
}

function _attributeFilter(attrName) {
    const isVideoConfigAttr = videoElementConfigAttributes.some((x) => x === attrName);

	return isVideoConfigAttr || attrName.match(/data-/);
}

function resetAttributes(element, originalAttributes) {
	for (let i = 0; i < element.attributes.length; i++) {
		let currAttr = element.attributes[i];
		if (!_attributeFilter(currAttr.name) && originalAttributes[currAttr.name] === undefined) {
			element.removeAttribute(currAttr.name);
		}
	}

	Object.keys(originalAttributes).forEach((attribute) => {
        element.setAttribute(attribute, originalAttributes[attribute]);
    });
}

function getAttributesFromElement(element) {
    const attrObj = [];
	for (let i = 0; i < element.attributes.length; i++) {
		if (!_attributeFilter(element.attributes[i].name)) {
			attrObj[element.attributes[i].name] = element.attributes[i].value;
		}
	}

    return attrObj;
}

function pullConfigFromVideoElement(videoElement) {
    const config = {};
	videoElementConfigAttributes.forEach((attrName) => {
		if (videoElement.hasAttribute(attrName)) {
			const attrValue = videoElement.getAttribute(attrName);

			if (attrName.match(/data-video/)) {
			    attrName = attrName.replace(/data-video-/, '');
            } else {
				videoElement.removeAttribute(attrName);
				videoElement.setAttribute('data-video-' + attrName, attrValue);
			}

			config[attrName] = attrValue !== '' ? attrValue : true;
		}
	});

    return config;
}

function initVideo(rootElement, autoplay) {

	let videoElement = Sizzle('video', rootElement);
	let	matchBreakpointCallback = function (name, isMatch) {
			Debug('matchBreakpointCallback', name, isMatch);
			if (isMatch) {
				domUtils.addClass(rootElement, name);
			} else {
				domUtils.removeClass(rootElement, name);
			}
		};

	if (!videoElement.length) {
		Debug.error('No video element found');

		return;
	}

	videoElement = videoElement[0];
	rootElement.originalAttributes = {
        root: getAttributesFromElement(rootElement),
        video: getAttributesFromElement(videoElement)
    };

	if (!_configured) {
		initConfig();
	}

	pluginsToAdd.forEach(({ plugin, prepend }) => {
		addPlugin(plugin, prepend);
	});
	pluginsToAdd.length = 0;

	const videoId = videoElement.getAttribute('data-video-id');

	if (!videoId) {
		Debug.error('No videoId given');

		return;
	}

	videoElement.setAttribute('data-rt', 'video-player');

	extend(videoElement, createEventWrapper());
	domUtils.addClass(rootElement, 'svp_container');
	domUtils.addClass(rootElement, 'svp_css-container--font');
	domUtils.addClass(videoElement, 'svp_video');

	if (Config.theme && Config.theme !== 'standard') {
		Config.theme.split(/,/).forEach(function (themeName) {
			domUtils.addClass(rootElement, 'svp_theme__' + themeName.trim());
		});
	}

	//Fix for iOS 7 bug that defaults video height to 150px
	if (device.isIOSLte7()) {
		const canvasNode = domUtils.fromString('<canvas class="svp_video">');
		rootElement.insertBefore(canvasNode, videoElement);
		videoElement.className += ' svp_video_ios7';
	}

	if (device.isWindowsPhone()) {
		rootElement.className += ' svp_container_wp';
	}

    // Build video config
    const videoConfig = Object.assign(
        {},
        defaultVideoConfig,
        pullConfigFromVideoElement(videoElement)
    );

	extend(rootElement, createEventWrapper());
	const api = new VideoWrapperApi(videoId, rootElement, videoConfig);
	_createApiProxy(api, rootElement);

	pluginManager.createAll(api);

	instances.push({ id: videoId, api, rootElement });

	//register breakpoint listeners
    if (Config.breakPoints) {
        Config.breakPoints.forEach((bp) => {
            Object.keys(bp).forEach((className) => {
                breakpoints.addListener(videoElement, className, bp[className], matchBreakpointCallback);
            });
        });
    }

	if (!api.config.preload || api.config.preload !== 'none' || api.config.autoplay) {
		rootElement.load(videoId, autoplay);
	}
}

function initVideoNoJs(videoContainerEl) {
	const videoEl = Sizzle('video', videoContainerEl)[0];
    const posterImage = videoEl.getAttribute('poster');
    const videoId = videoEl.getAttribute('data-video-id');
    const url = Config.nojsPlayerBaseUrl + videoId;

	videoContainerEl.className += ' svp_container svp_is--error';

	let linkEl = domUtils.fromString(
		'<div class="svp_video-container">\
			<img class="svp_ui-error__poster svp_video" src="' + posterImage + '"/>\
			<div class="svp_ui-error svp_ui-error__is--visible svp_ui-error__is--nojs"> \
				<div class="svp_ui-error__inner">\
					<span class="svp_ui-error__icon svp_ui-error__icon--blocked svp_is--visible"></span>\
					<div class="svp_ui-error__message-container">\
						<span class="svp_ui-error__message" aria-live="assertive" role="alert">Vi rekommenderar att du uppdaterar din webbläsare</span>\
						<div class="svp_ui-error__message-link--container">\
							<span class="svp_ui-error__message-icon--arrow"></span>\
							<a href="' + url + '" target="_blank">Inte nu, försök spela ändå</a>\
						</div>\
					</div>\
				</div>\
			</div>\
		</div>'
	);

	videoContainerEl.replaceChild(linkEl, videoEl);
}

function destroyElement(element) {
	if (element.destroy) {
		element.destroy();
	}
	if (element.children) {
		for (let i = 0; i < element.children.length; i++) {
			destroyElement(element.children[i]);
		}
	}
}

function destroy(rootElement) {
    // Lookup and remove instance
    let instance;
    for (let i = 0; i < instances.length; i++) {
        if (instances[i].rootElement === rootElement) {
            instance = instances[i];
            instances.splice(i, 1);
            break;
        }
    }

    if (!instance) {
        Debug.error('Destroy failed because an instance with the specified video id was not found');

        return;
    }


    const { api } = instance;

	pluginManager.destroyAll(api);

	destroyElement(rootElement);

	let videoElement = Sizzle('video', rootElement)[0];
    let originalAttributes = rootElement.originalAttributes;

	if (device.isIOSLte7()) {
		let canvasNode = Sizzle('canvas', rootElement)[0];
		rootElement.removeChild(canvasNode);
	}

	domUtils.removeClass(rootElement, 'svp_container');
	domUtils.removeClass(videoElement, 'svp_video');

	if (originalAttributes) {
	    resetAttributes(rootElement, originalAttributes.root);
	    resetAttributes(videoElement, originalAttributes.video);
	}

	breakpoints.destroy(videoElement);
}

function addPlugin(plugin, prepend = false) {
	if (_configured) {
		pluginManager.addPlugin(plugin, prepend);
	} else {
		pluginsToAdd.push({ plugin, prepend });
	}
}

function noop() {
    //
}

const forceNoJs = window.location.search.match(/nojs=true/);
const noJsMode = !document.addEventListener || forceNoJs;

module.exports = {
	destroy: destroy,
	initialize: noJsMode ? initVideoNoJs : initVideo,
	addPlugin: noJsMode ? noop : addPlugin
};




// WEBPACK FOOTER //
// ./src/js/modules/video.js